import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { resolveAuthToken } from "../utils/resolveAuthToken";
import { useAuth } from "./AuthContext";
import { useAppShell } from "./AppShellContext";
import { usePreview } from "./PreviewContext";
import type { Audience } from "../components/audience";

interface Project {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  isPublished?: boolean;
  role: "admin" | "editor" | "viewer";
  // Derived server-side (project_access.audience_of); /api/projects returns
  // both. Optional because a trimmed object must fall back to "private".
  audience?: Audience;
  partner?: { id: string; slug: string; name: string } | null;
}

interface ProjectContextValue {
  currentProject: Project | null;
  projects: Project[];
  loading: boolean;
  switchProject: (slug: string) => void;
  refreshProjects: () => Promise<void>;
  userRole: "admin" | "editor" | "viewer" | null;
  isInternal: boolean;
  isAdmin: boolean;
  canEdit: boolean;
}

const ProjectContext = createContext<ProjectContextValue | null>(null);
const PROJECT_KEY = "cortex_current_project";

export function ProjectProvider({ children }: { children: ReactNode }) {
  const { user, accessToken, isAuthenticated } = useAuth();
  const { currentApp } = useAppShell();
  const { isLocalPreview } = usePreview();
  const location = useLocation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);

  // Detect docs app from URL path — avoids waiting for AppShellProvider to resolve.
  // Share-link visitors live under `/share/<token>/...`; shares are always
  // docs surfaces, so treat any share-prefixed path as docs.
  const isDocsPath = location.pathname.startsWith("/docs") || /^\/share\/[^/]+/.test(location.pathname);

  const fetchProjects = useCallback(async (token?: string) => {
    // resolveAuthToken, not the raw accessToken: this runs again once /api/me
    // resolves and would otherwise refetch with the OPERATOR's localStorage
    // token, overwriting the impersonated project list with the whole tenant.
    const t = token || resolveAuthToken().token || accessToken;
    if (!t) return;
    try {
      const isDocsApp = currentApp?.slug === "docs" || isDocsPath;
      const url = isDocsApp ? "/api/projects?published_only=true" : "/api/projects";
      const res = await fetch(url, {
            headers: { Authorization: `Bearer ${t}` },
          });
      if (!res.ok) { setLoading(false); return; }
      const data: Project[] = await res.json();
      setProjects(data);

      // Restore from localStorage or pick first
      const saved = localStorage.getItem(PROJECT_KEY);
      const match = saved ? data.find((p) => p.slug === saved) : null;
      setCurrentProject(match || data[0] || null);
    } catch { /* ignore */ }
    setLoading(false);
  }, [accessToken, currentApp?.slug, isDocsPath]);

  // Fire immediately with token from localStorage — don't wait for AuthProvider to resolve
  useEffect(() => {
    const token = resolveAuthToken().token ?? accessToken;
    if (token) fetchProjects(token);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when auth resolves (handles token refresh / new login)
  useEffect(() => {
    if (isAuthenticated && accessToken) fetchProjects();
  }, [isAuthenticated, fetchProjects]);

  // Listen for project-switch events from CLI (via preview server SSE)
  useEffect(() => {
    if (!isLocalPreview) return;
    const handler = (e: Event) => {
      const type = (e as CustomEvent)?.detail?.type;
      if (type === "project-switch") {
        fetch("/api/preview-config").then(r => r.json()).then(cfg => {
          if (cfg?.projectSlug && cfg.projectSlug !== currentProject?.slug) {
            window.location.href = `/studio/projects/${cfg.projectSlug}`;
          }
        }).catch(() => {});
      }
    };
    window.addEventListener("preview-reload", handler);
    return () => window.removeEventListener("preview-reload", handler);
  }, [isLocalPreview, currentProject?.slug]);

  const switchProject = useCallback((slug: string) => {
    const proj = projects.find((p) => p.slug === slug);
    if (proj) {
      setCurrentProject(proj);
      localStorage.setItem(PROJECT_KEY, slug);
      // Sync to CLI workspace config in preview mode
      if (isLocalPreview) {
        fetch("/api/preview/switch-project", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ slug }),
        }).catch(() => {});
      }
    }
  }, [projects, isLocalPreview]);

  const userRole = currentProject?.role ?? null;
  const isInternal = user?.userType === "internal";
  const isSuperAdmin = user?.isSuperAdmin ?? false;
  const isAdmin = isSuperAdmin || (userRole === "admin" && isInternal);
  const canEdit = isSuperAdmin || (isInternal && (userRole === "admin" || userRole === "editor"));

  return (
    <ProjectContext.Provider value={{
      currentProject, projects: isDocsPath ? projects.filter(p => p.isPublished !== false) : projects,
      loading, switchProject, refreshProjects: fetchProjects,
      userRole, isInternal, isAdmin, canEdit,
    }}>
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject(): ProjectContextValue {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error("useProject must be used within <ProjectProvider>");
  return ctx;
}
